import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  Star,
  Loader2,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Search,
  Calendar,
  Building,
  GraduationCap,
  Sparkles,
  DollarSign,
  History,
  X,
  RefreshCw,
} from "lucide-react";
import { careersService } from "@/api/careersService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const APPLICATION_STATUSES = [
  "Applied",
  "Under Review",
  "Shortlisted",
  "Interview",
  "Selected",
  "Rejected",
  "Withdrawn",
];

// Light-theme status configs
const STATUS_CONFIG = {
  Applied: {
    label: "Applied",
    icon: Clock,
    classes: "bg-blue-50 text-blue-700 border-blue-200",
  },
  "Under Review": {
    label: "Under Review",
    icon: Eye,
    classes: "bg-amber-50 text-amber-700 border-amber-200",
  },
  Shortlisted: {
    label: "Shortlisted",
    icon: Star,
    classes: "bg-purple-50 text-purple-700 border-purple-200",
  },
  Interview: {
    label: "Interview",
    icon: Users,
    classes: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
  Selected: {
    label: "Selected",
    icon: CheckCircle2,
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  Rejected: {
    label: "Rejected",
    icon: XCircle,
    classes: "bg-rose-50 text-rose-700 border-rose-200",
  },
  Withdrawn: {
    label: "Withdrawn",
    icon: XCircle,
    classes: "bg-slate-100 text-slate-600 border-slate-200",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Applied;
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wider border",
        cfg.classes
      )}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function AdminJobApplications() {
  const { id: routeJobId } = useParams(); // May be undefined if on /admin/careers/applications
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedJobId, setSelectedJobId] = useState(routeJobId || "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Review Modal state
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewStatus, setReviewStatus] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [statusHistory, setStatusHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [updatingReview, setUpdatingReview] = useState(false);
  const [generatingResume, setGeneratingResume] = useState(false);
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);

  // Update selectedJobId if route changes
  useEffect(() => {
    if (routeJobId) {
      setSelectedJobId(routeJobId);
    }
  }, [routeJobId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appsData, jobsData] = await Promise.all([
        careersService.getApplications({
          jobId: selectedJobId !== "all" ? selectedJobId : null,
          status: statusFilter,
          search: searchTerm,
        }),
        careersService.getAllJobsAdmin(),
      ]);

      setApplications(appsData || []);
      setJobs(jobsData || []);
    } catch (err) {
      console.error("[AdminJobApplications] Load error:", err);
      toast.error(err.message || "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  }, [selectedJobId, statusFilter, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open review modal
  const handleOpenReview = async (app) => {
    setSelectedApp(app);
    setReviewStatus(app.status);
    setInternalNotes(app.internal_notes || "");
    setStatusHistory([]);

    try {
      setLoadingHistory(true);
      const history = await careersService.getApplicationStatusHistory(app.id);
      setStatusHistory(history || []);
    } catch (err) {
      console.warn("Could not load status history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Save review changes (status + internal notes)
  const handleSaveReview = async () => {
    if (!selectedApp) return;

    try {
      setUpdatingReview(true);
      await careersService.updateApplicationReview(
        selectedApp.id,
        reviewStatus,
        internalNotes
      );

      toast.success(`Application updated to "${reviewStatus}".`);

      // Update state locally
      setApplications((prev) =>
        prev.map((a) =>
          a.id === selectedApp.id
            ? { ...a, status: reviewStatus, internal_notes: internalNotes }
            : a
        )
      );

      setSelectedApp((prev) =>
        prev ? { ...prev, status: reviewStatus, internal_notes: internalNotes } : null
      );

      // Refresh history
      const updatedHistory = await careersService.getApplicationStatusHistory(selectedApp.id);
      setStatusHistory(updatedHistory || []);
    } catch (err) {
      toast.error(err.message || "Failed to update application review.");
    } finally {
      setUpdatingReview(false);
    }
  };

  // View resume via signed URL
  const handleViewResume = async (storagePath, applicantName) => {
    try {
      setGeneratingResume(true);
      const signedUrl = await careersService.createResumeSignedUrl(storagePath, 3600);
      if (!signedUrl) {
        throw new Error("Could not generate secure resume link.");
      }
      toast.info(`Opening resume for ${applicantName} (secure 1-hour link)...`);
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.message || "Failed to open resume document.");
    } finally {
      setGeneratingResume(false);
    }
  };

  // View cover letter via signed URL
  const handleViewCoverLetter = async (storagePath, applicantName) => {
    try {
      setGeneratingCoverLetter(true);
      const signedUrl = await careersService.createCoverLetterSignedUrl(storagePath, 3600);
      if (!signedUrl) {
        throw new Error("Could not generate secure cover letter link.");
      }
      toast.info(`Opening cover letter for ${applicantName} (secure 1-hour link)...`);
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.message || "Failed to open cover letter document.");
    } finally {
      setGeneratingCoverLetter(false);
    }
  };

  // Derived Metrics
  const metrics = {
    total: applications.length,
    applied: applications.filter((a) => a.status === "Applied").length,
    underReview: applications.filter((a) => a.status === "Under Review").length,
    interview: applications.filter((a) => a.status === "Interview").length,
    selected: applications.filter((a) => a.status === "Selected").length,
    rejected: applications.filter((a) => a.status === "Rejected").length,
  };

  const currentJobTitle =
    selectedJobId !== "all"
      ? jobs.find((j) => j.id === selectedJobId)?.title || "Selected Position"
      : "All Roles";

  return (
    <div className="space-y-6 select-none">
      {/* Back to Careers link if scoped */}
      {routeJobId && (
        <div>
          <Link
            to="/admin/careers"
            className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Career Positions</span>
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-semibold uppercase tracking-wider mb-2">
            <Users className="w-3.5 h-3.5" />
            <span>Applicant Pipeline</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            Applications: {currentJobTitle}
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs font-mono font-normal">
              {applications.length} candidates
            </Badge>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 font-medium">
            Review candidate resumes, evaluate credentials, update pipeline stages, and log interview notes.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs h-9 gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5 text-cyan-600" />
          Refresh Data
        </Button>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-slate-900 font-mono">{metrics.total}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1 font-medium">Total</div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-blue-700 font-mono">{metrics.applied}</div>
          <div className="text-[11px] text-blue-600 uppercase tracking-wider mt-1 font-medium">New Applied</div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-amber-700 font-mono">{metrics.underReview}</div>
          <div className="text-[11px] text-amber-600 uppercase tracking-wider mt-1 font-medium">Under Review</div>
        </div>

        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-cyan-700 font-mono">{metrics.interview}</div>
          <div className="text-[11px] text-cyan-600 uppercase tracking-wider mt-1 font-medium">Interviewing</div>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-emerald-700 font-mono">{metrics.selected}</div>
          <div className="text-[11px] text-emerald-600 uppercase tracking-wider mt-1 font-medium">Selected</div>
        </div>

        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-center shadow-xs">
          <div className="text-2xl font-bold text-rose-700 font-mono">{metrics.rejected}</div>
          <div className="text-[11px] text-rose-600 uppercase tracking-wider mt-1 font-medium">Rejected</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between shadow-xs">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search candidate name, email, or position..."
            className="pl-10 h-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl text-xs focus:bg-white"
          />
        </div>

        {/* Position Filter */}
        <div className="flex items-center gap-2">
          <Briefcase className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            className="h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
          >
            <option value="all" className="bg-white text-slate-900">All Positions & General</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id} className="bg-white text-slate-900">
                {j.title}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center rounded-xl bg-slate-100 p-1 border border-slate-200 overflow-x-auto text-xs gap-0.5">
          {["all", ...APPLICATION_STATUSES].map((st) => (
            <button
              key={st}
              type="button"
              onClick={() => setStatusFilter(st)}
              className={cn(
                "px-2.5 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap",
                statusFilter === st
                  ? "bg-cyan-600 text-white font-semibold shadow-xs"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              )}
            >
              {st === "all" ? "All Stages" : st}
            </button>
          ))}
        </div>
      </div>

      {/* Applications Table */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-16 rounded-2xl bg-slate-100 border border-slate-200" />
          ))}
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-slate-200 bg-white p-8 space-y-3 shadow-xs">
          <Users className="w-8 h-8 text-slate-300 mx-auto" />
          <h3 className="text-base font-semibold text-slate-900">No applications match your criteria</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchTerm || statusFilter !== "all" || selectedJobId !== "all"
              ? "Try adjusting your search terms or filter selections."
              : "When candidates submit their resumes, they will appear in this pipeline."}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="py-3.5 px-4">Candidate</th>
                  <th className="py-3.5 px-4">Role Applied For</th>
                  <th className="py-3.5 px-4">Experience / Notice</th>
                  <th className="py-3.5 px-4">Expected Comp</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Submitted</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                    {/* Candidate */}
                    <td className="py-4 px-4">
                      <div className="font-semibold text-slate-900 text-sm">
                        {app.first_name} {app.last_name}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3 h-3 text-cyan-600" />
                        <span>{app.email}</span>
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <Phone className="w-3 h-3 text-slate-300" />
                        <span>{app.phone}</span>
                        <span>•</span>
                        <MapPin className="w-3 h-3 text-slate-300" />
                        <span>{app.location}</span>
                      </div>
                    </td>

                    {/* Role Applied For */}
                    <td className="py-4 px-4">
                      <div className="font-medium text-slate-900">
                        {app.job_title_snapshot}
                      </div>
                      {app.job_id ? (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {app.jobs?.departments?.name || "Direct Position"}
                        </div>
                      ) : (
                        <Badge className="mt-1 bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                          General Application
                        </Badge>
                      )}
                    </td>

                    {/* Experience & Notice */}
                    <td className="py-4 px-4">
                      <div className="text-slate-800 font-medium">{app.experience_level}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Notice: {app.notice_period}
                      </div>
                    </td>

                    {/* Expected Compensation */}
                    <td className="py-4 px-4 font-mono text-slate-700">
                      {app.expected_salary}
                    </td>

                    {/* Status Badge */}
                    <td className="py-4 px-4">
                      <StatusBadge status={app.status} />
                    </td>

                    {/* Date Submitted */}
                    <td className="py-4 px-4 text-slate-400 font-mono text-[11px]">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>

                    {/* Review Actions */}
                    <td className="py-4 px-4 text-right">
                      <Button
                        size="sm"
                        onClick={() => handleOpenReview(app)}
                        variant="outline"
                        className="rounded-xl border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs h-8 px-3 gap-1.5"
                      >
                        <Eye className="w-3.5 h-3.5 text-cyan-600" />
                        <span>Review</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Candidate Review Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          {/* Light-themed dialog; overlay above is intentionally dark */}
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-200 pb-5">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    {selectedApp.first_name} {selectedApp.last_name}
                  </h2>
                  <StatusBadge status={selectedApp.status} />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Applied for{" "}
                  <span className="text-cyan-600 font-semibold">{selectedApp.job_title_snapshot}</span>{" "}
                  on {new Date(selectedApp.created_at).toLocaleString()}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedApp(null)}
                className="p-1.5 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Overview Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block">
                  Contact
                </span>
                <div className="text-slate-900 font-semibold">{selectedApp.email}</div>
                <div className="text-slate-600">{selectedApp.phone}</div>
                <div className="text-slate-500">{selectedApp.location}</div>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block">
                  Experience & Notice
                </span>
                <div className="text-slate-900 font-semibold">{selectedApp.experience_level}</div>
                <div className="text-slate-600">Notice: {selectedApp.notice_period}</div>
                <div className="text-slate-500">Mode: {selectedApp.work_mode_preference}</div>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold block">
                  Compensation
                </span>
                <div className="text-slate-900 font-semibold">Exp: {selectedApp.expected_salary}</div>
                {selectedApp.current_salary && (
                  <div className="text-slate-600">Curr: {selectedApp.current_salary}</div>
                )}
                {selectedApp.availability_date && (
                  <div className="text-slate-500">Avail: {selectedApp.availability_date}</div>
                )}
              </div>
            </div>

            {/* Resume Document Link (Signed URL) */}
            <div className="flex flex-wrap items-center justify-between p-4 rounded-2xl bg-cyan-50 border border-cyan-200 gap-3">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-cyan-600" />
                <div>
                  <div className="text-xs font-semibold text-slate-900">
                    {selectedApp.resume_filename || "Candidate Resume.pdf"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {selectedApp.resume_storage_path}
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={() =>
                  handleViewResume(
                    selectedApp.resume_storage_path,
                    `${selectedApp.first_name} ${selectedApp.last_name}`
                  )
                }
                disabled={generatingResume}
                className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold gap-1.5 h-9 px-4 shadow-sm"
              >
                {generatingResume ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Generating Link...</span>
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>View Resume (Secure Link)</span>
                  </>
                )}
              </Button>
            </div>

            {/* Optional Cover Letter Link (Signed URL) */}
            {selectedApp.cover_letter_storage_path && (
              <div className="flex flex-wrap items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-200 gap-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <div>
                    <div className="text-xs font-semibold text-slate-900">
                      {selectedApp.cover_letter_filename || "Candidate Cover Letter.pdf"}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {selectedApp.cover_letter_storage_path}
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() =>
                    handleViewCoverLetter(
                      selectedApp.cover_letter_storage_path,
                      `${selectedApp.first_name} ${selectedApp.last_name}`
                    )
                  }
                  disabled={generatingCoverLetter}
                  variant="outline"
                  className="rounded-xl border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold gap-1.5 h-9 px-4 shadow-sm"
                >
                  {generatingCoverLetter ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Generating Link...</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>View Cover Letter</span>
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Social / Portfolio Links */}
            {(selectedApp.portfolio_url || selectedApp.linkedin_url || selectedApp.github_url) && (
              <div className="flex flex-wrap gap-2 text-xs">
                {selectedApp.portfolio_url && (
                  <a
                    href={selectedApp.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    <span>Portfolio</span>
                    <ExternalLink className="w-3 h-3 text-cyan-600" />
                  </a>
                )}
                {selectedApp.linkedin_url && (
                  <a
                    href={selectedApp.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    <span>LinkedIn Profile</span>
                    <ExternalLink className="w-3 h-3 text-cyan-600" />
                  </a>
                )}
                {selectedApp.github_url && (
                  <a
                    href={selectedApp.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    <span>GitHub Profile</span>
                    <ExternalLink className="w-3 h-3 text-cyan-600" />
                  </a>
                )}
              </div>
            )}

            {/* Technical Skills & Education */}
            <div className="space-y-3">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1 text-xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Technical Competencies & Skills
                </span>
                <p className="text-slate-800 whitespace-pre-wrap">{selectedApp.skills}</p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1 text-xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Education Background
                </span>
                <p className="text-slate-800 whitespace-pre-wrap">{selectedApp.education}</p>
              </div>

              {selectedApp.motivation && (
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1 text-xs">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Candidate Motivation Statement
                  </span>
                  <p className="text-slate-800 whitespace-pre-wrap">{selectedApp.motivation}</p>
                </div>
              )}
            </div>

            {/* Review Section */}
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50/40 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                <span>Recruitment Decision & Review Notes</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-700 font-semibold block mb-1">
                    Pipeline Status
                  </label>
                  <select
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    className="w-full h-9 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    {APPLICATION_STATUSES.map((st) => (
                      <option key={st} value={st} className="bg-white text-slate-900">
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-700 font-semibold block mb-1">
                    Internal Notes / Interview Feedback
                  </label>
                  <Textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Candidate strengths, interview notes, compensation agreements..."
                    rows={2}
                    className="bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs rounded-xl"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveReview}
                  disabled={updatingReview}
                  className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold gap-2 h-9 px-5 shadow-sm"
                >
                  {updatingReview ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Saving Review...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Update Review & Status</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Status History Timeline */}
            <div className="space-y-3 pt-2">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-cyan-600" />
                <span>Automated Status Transition History</span>
              </h3>

              {loadingHistory ? (
                <div className="text-xs text-slate-400">Loading history audit trail...</div>
              ) : statusHistory.length === 0 ? (
                <div className="text-xs text-slate-400">No historical status transitions recorded.</div>
              ) : (
                <div className="space-y-2">
                  {statusHistory.map((hist) => (
                    <div
                      key={hist.id}
                      className="flex items-start justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/50 text-xs"
                    >
                      <div className="space-y-0.5">
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          <span className="text-slate-500">{hist.previous_status || "None"}</span>
                          <span className="text-cyan-600">→</span>
                          <StatusBadge status={hist.new_status} />
                        </div>
                        {hist.notes && (
                          <div className="text-[11px] text-slate-500 italic">"{hist.notes}"</div>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {new Date(hist.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
